// AdminSidebar: Slide-in/out menu for admin panel
import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemIcon, ListItemText, Tooltip, ListItemButton, Badge } from '@mui/material';
import api from '../services/api';
import { useProfile } from '../context/ProfileContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import BookIcon from '@mui/icons-material/Book';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import { useLocation, Link } from 'react-router-dom';

const allMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin', roles: ['admin', 'superadmin'] },
  { text: 'Events', icon: <EventIcon />, path: '/admin/events', roles: ['admin', 'superadmin'] },
  { text: 'Users', icon: <PeopleIcon />, path: '/admin/users', roles: ['admin', 'superadmin'] },
  { text: 'Packages', icon: <CategoryIcon />, path: '/admin/packages', roles: ['admin', 'superadmin'] },
  { text: 'Bookings', icon: <BookIcon />, path: '/admin/bookings', roles: ['admin', 'superadmin'] },
  { text: 'Tickets', icon: <ConfirmationNumberIcon />, path: '/admin/tickets', roles: ['admin', 'superadmin'] },
  { text: 'Payments', icon: <PaymentIcon />, path: '/admin/payments', roles: ['admin', 'superadmin'] },
  { text: 'Notifications', icon: <NotificationsIcon />, path: '/admin/notifications', roles: ['admin', 'superadmin'] },
  { text: 'CMS', icon: <CategoryIcon />, path: '/admin/cms', roles: ['admin', 'superadmin'] },
  { text: 'Reports', icon: <BarChartIcon />, path: '/admin/reports', roles: ['admin', 'superadmin'] },
  { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings', roles: ['admin', 'superadmin'] },
];

function AdminSidebar({ mini }) {
  const location = useLocation();
  const { profile: serverProfile } = useProfile() || {};
  // read local user if present and normalize role(s)
  let storedUser = serverProfile || {};
  if (!storedUser || Object.keys(storedUser).length === 0) {
    try { storedUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { storedUser = {}; }
  }
  const rawRole = storedUser && storedUser.role ? storedUser.role : '';
  const normalizeRoles = (r) => {
    if (!r) return [];
    if (Array.isArray(r)) return r.map(x => String(x).trim().toLowerCase()).filter(Boolean);
    if (typeof r === 'string') return r.split(/[,;|]/).map(x => String(x).trim().toLowerCase()).filter(Boolean);
    return [String(r).trim().toLowerCase()];
  };
  const storedRoles = normalizeRoles(rawRole);

  const computeMenuForRole = (rolesArray) => {
    const rolesSet = new Set((rolesArray || []).map(r => String(r).toLowerCase()));
    if (!rolesSet.size) return [];
    if (rolesSet.has('superadmin')) return allMenuItems.slice();
    if (rolesSet.has('admin')) return allMenuItems.slice();
    return allMenuItems.filter(item => Array.isArray(item.roles) && item.roles.some(r => rolesSet.has(String(r).toLowerCase())));
  };

  const [menuItems, setMenuItems] = useState(() => computeMenuForRole(storedRoles));

  useEffect(() => {
    // If no menu items and there is a token, try to fetch profile from API to recover role
    const token = localStorage.getItem('token');
    if ((menuItems == null || menuItems.length === 0) && token) {
      api.get('/api/user/profile')
        .then(res => {
          const roleVal = res.data?.role || '';
          const roles = normalizeRoles(roleVal);
          if (roles.length) setMenuItems(computeMenuForRole(roles));
          // persist normalized user back to localStorage for other components
          try {
            const normalized = { ...(res.data || {}) };
            localStorage.setItem('user', JSON.stringify(normalized));
          } catch (e) {}
        })
        .catch(() => {
          // ignore; leave menuItems empty so we show fallback message
        });
    }
  }, []); // run once

  // Do not auto-upgrade to full admin menu based on route alone; keep menu stable once set.
  return (
    <List sx={{ width: mini ? 60 : 240, bgcolor: 'background.paper', height: '100%', boxShadow: 2, transition: 'width 0.3s' }}>
          {menuItems.length === 0 ? (
            <ListItem>
              <ListItemText primary="No menu items available for your role" />
            </ListItem>
          ) : menuItems.map((item) => (
        <Tooltip title={mini ? item.text : ''} placement="right" arrow key={item.text}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                my: 0.5,
                bgcolor: location.pathname === item.path ? 'primary.main' : 'background.paper',
                color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                boxShadow: location.pathname === item.path ? 3 : 0,
                minHeight: 48,
                justifyContent: mini ? 'center' : 'flex-start',
                px: mini ? 1 : 2,
                transition: 'background 0.2s',
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.contrastText' : 'text.secondary', minWidth: 0, mr: mini ? 0 : 2, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
              {!mini && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      ))}
    </List>
  );
}

export default AdminSidebar;
